import { useEffect, useId, useState } from 'react';

/**
 * The site's palette as plain hex, since Mermaid can't read the oklch tokens
 * in styles/tokens.css. Kept close to them by eye; the site has no dark mode.
 */
const THEME = {
  background: '#fdfcfa',
  mainBkg: '#ffffff',
  primaryColor: '#ffffff',
  primaryTextColor: '#1f2430',
  primaryBorderColor: '#cfd3db',
  nodeBorder: '#cfd3db',
  lineColor: '#5f6573',
  textColor: '#1f2430',
  edgeLabelBackground: '#fdfcfa',
  clusterBkg: '#f6f5f2',
  clusterBorder: '#e2e4e9',
  fontFamily: 'inherit',
  fontSize: '14px',
};

let loading = null;
/** Mermaid is large, so it is fetched the first time a chart is shown: admin pages only. */
function loadMermaid() {
  loading ??= import('mermaid').then(({ default: mermaid }) => {
    mermaid.initialize({
      startOnLoad: false,
      securityLevel: 'strict',
      theme: 'base',
      themeVariables: THEME,
      flowchart: { useMaxWidth: false },
    });
    return mermaid;
  });
  return loading;
}

/**
 * One Mermaid chart. A chart that fails to parse shows its source and the
 * error, so a mistake in diagrams.js is visible rather than a blank space.
 */
export function Mermaid({ chart, label }) {
  const id = `m${useId().replace(/[^a-zA-Z0-9]/g, '')}`;
  const [svg, setSvg] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setError('');
    loadMermaid()
      .then((mermaid) => mermaid.render(id, chart))
      .then(({ svg: out }) => !cancelled && setSvg(out))
      .catch((err) => {
        // A failed render leaves its scratch element behind.
        document.getElementById(`d${id}`)?.remove();
        if (!cancelled) setError(err?.message ?? String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [chart, id]);

  if (error) {
    return (
      <div className="rounded-md border border-negative/25 bg-negative-soft p-3 text-sm text-negative">
        <p className="font-medium">This chart didn’t render: {error}</p>
        <pre className="mt-2 overflow-x-auto text-xs text-ink">{chart}</pre>
      </div>
    );
  }
  return (
    <div
      role="img"
      aria-label={label}
      className="overflow-x-auto [&_svg]:mx-auto [&_svg]:max-w-none"
      // Mermaid's own output, rendered with securityLevel 'strict' from source in this repo.
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}
